import { createContext, useContext } from 'react';

/**
 * The register of modal UI that is on screen anywhere in the app, so a gate in
 * one tree (the session-wide `SessionGates`) and a modal in another (a
 * Dashboard tour, a promo) can each ask "is something already blocking?"
 * without knowing about the other. Both trees `claim` while their UI is up
 * and `release` when it goes.
 */
export interface SessionGatesContextValue {
  /** Register `id` as on screen. Returns the matching release. */
  claim: (id: string) => () => void;
  release: (id: string) => void;
  /** Whether anything at all is claimed. */
  isBlocked: boolean;
  /** Which ids are claimed, for callers that care about one in particular. */
  blockingIds: readonly string[];
  /**
   * Every id claimed at any point this session, still listed after release.
   * The promos yield to anything that has already had the student's attention
   * this session, not only to what is on screen now.
   */
  shownIds: readonly string[];
}

const noop = () => undefined;

/**
 * Outside the provider — component tests that mount a page on its own —
 * nothing is ever blocking and claims are no-ops, so a page's own gating
 * behaves the way it did before the register existed.
 */
export const SessionGatesContext = createContext<SessionGatesContextValue>({
  claim: () => noop,
  release: noop,
  isBlocked: false,
  blockingIds: [],
  shownIds: [],
});

export const useSessionGates = () => useContext(SessionGatesContext);
